export class ColorUtils {

  public static getLuminance(hex: string): number {
    const rgb = this.hexToRgb(hex);
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  }

  public static hexToRgb(hex: string): { r: number, g: number, b: number } {
    const bigint = parseInt(hex.substring(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return {r, g, b};
  }

  public static rgbToHex(r: number, g: number, b: number): string {
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  public static getContrastColor(hex: string, alpha = 0): string {
    const rgb = this.hexToRgb(hex);
    const luminance = this.getLuminance(hex);
    const blendColor = luminance > 0.5 ? 0 : 255;

    const blend = (color: number, alpha: number, blendColor: number): number => {
      return Math.round(color * (1 - alpha) + blendColor * alpha);
    }

    const newR = blend(rgb.r, alpha, blendColor);
    const newG = blend(rgb.g, alpha, blendColor);
    const newB = blend(rgb.b, alpha, blendColor);

    return this.rgbToHex(newR, newG, newB);
  }

  public static getInitialsColor(initials: string): string {
    initials = initials.trim().toUpperCase();
    let hash = 0;

    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    const saturation = 70;
    const lightness = 60;
    const h = hue / 360;
    const s = saturation / 100;
    const l = lightness / 100;

    let r: number, g: number, b: number;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number): number => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x: number) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.padStart(2, '0');
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  public static adjustColor(hex: string, percent: number): string {
    const adjust = (c: number): number => {
      return Math.min(
        255,
        Math.max(0, Math.round(c + ((ColorUtils.isLightColor(hex) ? -percent : percent) / 100) * 255))
      );
    };
    const hexToRgb = ColorUtils.hexToRgb(hex);

    return ColorUtils.rgbToHex(adjust(hexToRgb.r), adjust(hexToRgb.g), adjust(hexToRgb.b));
  }

  public static isLightColor(color: string): boolean {
    return ColorUtils.getLuminance(color) > .5;
  }
}
